/** FLD-OFF — axios plumbing shared by the field and drawings clients.
 *  Reads: every successful GET is cached; a GET that cannot reach the server is answered from the
 *  cache (stamped `fromCache` / `cachedAt`). Writes: every mutating request carries a client write
 *  id; one that cannot reach the server is queued and answered 202 `{queued, client_id}`. */
import { idb, uuid } from "./db";
import { enqueue, registerClient, setOnline, isOnline } from "./outbox";
import { isManualOffline, noteResult } from "./quality";
import { classify, isLinkDown, noteInconclusive } from "./link";
import { shrink } from "./images";
import { retryDelayMs, shouldRetry, sleep } from "./retry";

const MUTATING = new Set(["post", "patch", "put", "delete"]);
/** A field tap must never wait on a weak link: the request is abandoned in seconds and the write
 *  goes to the outbox instead. Photos get longer because the body is big. */
export const FIELD_TIMEOUT_MS = 4500;
export const UPLOAD_TIMEOUT_MS = 60000;
/** FLD-OFF-FIX-3 §2 — requests whose own server step is long or whose body is large. They still
 *  queue and still surface their errors; what they may NOT do is testify about the connection,
 *  because "slow" and "disconnected" are different facts. Marked centrally so no call site has to
 *  remember. */
const HEAVY = ["/frs-items", "/frs-grid", "/material/status", "/completions", "/offline/projects",
  "/uploads", "/sheets", "/projects", "/delivery", "/picking-tickets", "/scheduling", "/frs-dates"];
const isHeavyUrl = (u) => HEAVY.some((h) => String(u || "").startsWith(h));
const cacheKey = (name, cfg) => `${name}:${cfg.url}${cfg.params ? "?" + new URLSearchParams(Object.entries(cfg.params).filter(([, v]) => v != null && v !== "")).toString() : ""}`;
const timedOut = (e) => e.code === "ECONNABORTED" || e.code === "ETIMEDOUT" || /timeout/i.test(e.message || "");
const netError = (e) => !e.response && (e.code === "ERR_NETWORK" || timedOut(e) || e.message === "Network Error" || !isOnline());

const projectOf = (cfg) => {
  const b = cfg.data instanceof FormData ? Object.fromEntries(cfg.data.entries()) : (typeof cfg.data === "string" ? JSON.parse(cfg.data || "{}") : cfg.data || {});
  return b.project_id || cfg.params?.project_id || (String(cfg.url).match(/projects\/([0-9a-f-]{36})/) || [])[1] || cfg._project_id || "";
};
const kindOf = (cfg) => {
  const u = String(cfg.url);
  if (u.includes("/completions/photos") || u.includes("/photo-pin")) return "photo";
  // FT-2 §6 — material photos ride the same queue: thumbnail local, upload deferred, and the
  // record is only "synced" once the server confirms the file is stored.
  if (u.includes("/material/photos")) return "photo";
  if (u.includes("/completions")) return "completion";
  if (u.includes("/placements")) return "placement";
  if (u.includes("/returns")) return "return";
  if (u.includes("/picking-tickets") || u.includes("/material/tickets")) return "ticket";
  if (u.includes("punch")) return "punch";
  if (u.includes("/frs-items")) return "readiness";
  if (u.includes("/notes")) return "note";
  if (u.includes("/markups") || u.includes("/drawings") || u.includes("/symbols/")) return "markup";
  return u.split("/").filter(Boolean).slice(-1)[0] || "write";
};

export let lastCachedAt = "";

export function installOffline(name, instance, hooks = {}) {
  registerClient(name, instance, hooks);
  instance.interceptors.request.use((cfg) => {
    const isUpload = typeof FormData !== "undefined" && cfg.data instanceof FormData;
    if (cfg.timeout === undefined || cfg.timeout === 0) cfg.timeout = isUpload ? UPLOAD_TIMEOUT_MS : FIELD_TIMEOUT_MS;
    cfg._t0 = Date.now();
    // FLD-OFF-FIX-3 — a big body or a long server step is never evidence about the link.
    if (isUpload || isHeavyUrl(cfg.url)) cfg._heavy = true;
    if (MUTATING.has((cfg.method || "get").toLowerCase())) {
      if (!cfg.headers["X-Client-Write-Id"]) cfg.headers["X-Client-Write-Id"] = uuid();
      if (!cfg.headers["X-Client-Timestamp"]) cfg.headers["X-Client-Timestamp"] = new Date().toISOString();
      if (!cfg.headers["X-Project-Id"]) cfg.headers["X-Project-Id"] = projectOf(cfg);
      if (!cfg.headers["X-Write-Kind"]) cfg.headers["X-Write-Kind"] = kindOf(cfg);
    }
    // Working offline (manual switch, or the detector proved the network is down): never touch it,
    // answer from the cache / outbox immediately. `_replay` and the worker ping are the ways out.
    if ((isManualOffline() || isLinkDown()) && !cfg._replay) {
      const err = Object.assign(new Error("Network Error"), { config: cfg, code: "ERR_NETWORK", _localFirst: true });
      throw err;
    }
    return cfg;
  });
  instance.interceptors.response.use(async (res) => {
    noteResult(true, Date.now() - (res.config?._t0 || Date.now()), { heavy: !!res.config?._heavy });
    setOnline(true);
    if ((res.config.method || "get").toLowerCase() === "get" && res.status === 200 && !res.config._noCache) {
      idb.set("api-cache", cacheKey(name, res.config), { data: res.data, cached_at: new Date().toISOString() });
      if (name === "field" && String(res.config.url).startsWith("/frs-items") && res.config.params?.project_id) frsFingerprint(res.config.params.project_id, res.data);
      if (hooks.overlay) res.data = await hooks.overlay(res.config, res.data);   // unsent writes stay visible until the replay lands
    }
    return res;
  }, async (err) => {
    const cfg = err.config || {};
    // FLD-OFF-FIX-3 — what the link learns from this error, if anything. An HTTP answer of ANY
    // status means the network is fine; a cancellation, a refused body size and a timeout taken
    // while this thread was stalled teach the detector nothing.
    const verdict = classify(err);
    if (verdict === "reached") noteResult(true, Date.now() - (cfg._t0 || Date.now()), { heavy: !!cfg._heavy });
    else if (verdict === "netfail") noteResult(false, 0, { critical: !!cfg._critical, timeout: timedOut(err),
      reason: `${err.code || err.message} on ${cfg.url || "?"}` });
    else if (verdict === "inconclusive" && !err._localFirst) noteInconclusive();
    // DRW-LOAD-C §1 — one more go before the cache or the outbox: storage stalls, the server was
    // busy, the dev server recompiled. Exponential backoff, GETs only, 3 attempts.
    const attempt = (cfg._attempt || 1);
    if (shouldRetry(cfg, verdict, attempt, isLinkDown() || isManualOffline())) {
      await sleep(retryDelayMs(attempt));
      return instance.request({ ...cfg, _attempt: attempt + 1, _t0: Date.now(),
        transformRequest: undefined, transformResponse: undefined });
    }
    if (!netError(err) || cfg._replay || cfg._noQueue) throw err;
    if (verdict === "netfail" && isLinkDown()) setOnline(false);
    const method = (cfg.method || "get").toLowerCase();
    if (method === "get") {
      const hit = await idb.get("api-cache", cacheKey(name, cfg));
      if (hit) {
        let data = hit.data;
        if (name === "field" && String(cfg.url).startsWith("/completions") && !String(cfg.url).includes("photos")) data = await overlayCompletions(data, cfg.params?.project_id);
        if (hooks.overlay) data = await hooks.overlay(cfg, data);
        lastCachedAt = hit.cached_at;
        return { data, status: 200, statusText: "OK (cache)", headers: { "x-cached-at": hit.cached_at }, config: cfg, fromCache: true, cachedAt: hit.cached_at };
      }
      throw err;
    }
    if (!MUTATING.has(method)) throw err;
    const id = cfg.headers["X-Client-Write-Id"] || uuid();
    const kind = kindOf(cfg);
    const body = cfg.data instanceof FormData ? cfg.data : (typeof cfg.data === "string" ? JSON.parse(cfg.data || "null") : cfg.data);
    // the record keeps the SAME id the request already carried, so a half-sent write never doubles
    let rec;
    try {
      rec = await enqueue({ id, client: name, method, url: cfg.url, body, params: cfg.params, project_id: projectOf(cfg), kind,
        label: describe(kind, cfg, body) });
    } catch (e) {
      if (e.device_full) throw Object.assign(e, { config: cfg, response: { status: 507, data: { detail: e.message } } });   // never a silent loss
      throw err;
    }
    let data = { queued: true, client_id: rec.id };
    if (kind === "photo" && body instanceof FormData) {
      const f = body.get("file");
      if (f instanceof Blob) { await idb.set("blobs", `thumb:${rec.id}`, await thumbnail(f)); }
      data = { id: rec.id, storage_path: `local:${rec.id}`, filename: f?.name || "photo.jpg", content_type: f?.type || "image/jpeg", size: f?.size || 0, uploader: body.get("uploader") || "", uploaded_at: rec.client_timestamp, is_deleted: false, local: true, queued: true };
    }
    if (kind === "completion" && body && !(body instanceof FormData)) await rememberCompletion(rec, body);
    if (hooks.onQueued) await hooks.onQueued(rec, rec.body, cfg);
    return { data, status: 202, statusText: "Queued", headers: {}, config: cfg, queued: true };
  });
}

/** Queue entries read like the installer talks: "Photo of Room A-201 — Misc Sink". */
function describe(kind, cfg, body) {
  const f = (k) => { try { return body instanceof FormData ? (body.get(k) || "") : (body?.[k] ?? cfg.params?.[k] ?? ""); } catch { return ""; } };
  const room = f("room_number") || f("room") || f("location_label") || f("location_key") || f("room_label") || "";
  const product = f("product_short") || f("product_code") || f("product") || f("product_name") || "";
  const what = { photo: "Photo", completion: "Install progress", placement: "Placement", return: "Return", ticket: "Ticket",
    punch: "Punch item", readiness: "Readiness", note: "Note", markup: "Drawing markup" }[kind] || (kind ? kind.replace(/[-_]/g, " ") : "Change");
  const where = [room ? `Room ${room}` : "", product].filter(Boolean).join(" — ");
  return where ? `${what} of ${where}` : what;
}

async function thumbnail(file) {
  // FLD-OFF-FIX-3 §3 — decode + resize in a worker; the main thread stays free.
  return shrink(file, 320, 0.7);
}

/** Optimistic completion: the installer sees the room they just marked, straight away. */
async function rememberCompletion(rec, body) {
  const key = `optimistic:${body.project_id}`;
  const cur = (await idb.get("meta", key)) || [];
  const pct = body.mode === "full" ? 1 : null;
  cur.push({ id: rec.id, kind: "completion", at: rec.client_timestamp, body, pct });
  await idb.set("meta", key, cur);
}

async function overlayCompletions(list, projectId) {
  if (!projectId || !Array.isArray(list)) return list;
  const ov = (await idb.get("meta", `optimistic:${projectId}`)) || [];
  let out = [...list];
  for (const o of ov.filter((x) => x.kind === "completion")) {
    const b = o.body;
    const same = (c) => ["scope_type", "building", "floor", "area", "room_number", "room_type"].every((k) => String(c[k] ?? "") === String(b[k] ?? ""));
    const prev = out.find(same);
    const rem = b.remaining_units_by_item || {};
    const remaining = Object.values(rem).reduce((a, v) => a + Number(v || 0), 0);
    const pct = b.mode === "full" ? 1 : (prev?.total_units ? Math.max(0, Math.min(1, (prev.total_units - remaining) / prev.total_units)) : (remaining ? 0.5 : 1));
    const rec = { ...(prev || {}), ...b, id: prev?.id || o.id, mode: b.mode, percent_complete: pct, remaining_units_by_item: rem,
      remaining_item_ids: Object.keys(rem), remaining_units: remaining, pending_sync: true, updated_at: o.at, photos: [...(prev?.photos || []), ...(b.photos || [])] };
    out = out.filter((c) => !same(c)).concat([rec]);
  }
  return out;
}

/** FRS revision that arrived while writes were queued → banner on the project, never a silent change. */
async function frsFingerprint(projectId, rows) {
  if (!Array.isArray(rows)) return;
  const fp = `${rows.length}:${rows.reduce((m, r) => (r.updated_at > m ? r.updated_at : m), "")}`;
  const key = `frs_fp:${projectId}`;
  const prev = await idb.get("meta", key);
  if (prev && prev !== fp) {
    const queued = (await idb.all("outbox")).some((r) => r.project_id === projectId);
    if (queued || (await idb.get("meta", `frs_changed:${projectId}`)) === "pending") await idb.set("meta", `frs_changed:${projectId}`, fp);
  }
  await idb.set("meta", key, fp);
}
export const frsChanged = (projectId) => idb.get("meta", `frs_changed:${projectId}`);
export const dismissFrsChanged = (projectId) => idb.del("meta", `frs_changed:${projectId}`);
